import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import Swal from "sweetalert2";

// Axios interceptor for adding authorization token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("app_atkey");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

interface DataBurdanFarmerState {
  data: any[];
  error: string;
  success: boolean;
  isLoading: boolean;
}

const initialState: DataBurdanFarmerState = {
  data: [],
  error: "",
  success: false,
  isLoading: false,
};

// Async thunks
export const getBurdanFarmerReducer = createAsyncThunk(
  "dataBurdanFarmer/getData",
  async (
    args: {
      url: string;
      page: number;
      size: number;
      searchValue?: string;
      crop_year_id: any;
    },
    { rejectWithValue }
  ) => {
    try {
      let { url, page, size, searchValue, crop_year_id } = args;
      console.log(url);

      const params = new URLSearchParams();

      // Add search parameter if searchValue exists
      if (searchValue && searchValue.trim() !== "") {
        params.append("search", searchValue.trim());
      }

      // Add pagination parameters
      params.append("page", page.toString());
      params.append("size", size.toString());
      params.append("crop_year_id", crop_year_id);

      // Add params to URL
      const separator = url.includes("?") ? "&" : "?";
      url += `${separator}${params.toString()}`;

      const response = await axios.get(url);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addBurdanFarmerReducer = createAsyncThunk(
  "dataBurdanFarmer/addData",
  async (data: any, { rejectWithValue }) => {
    console.log(data, "data");

    try {
      const response = await axios.post(
        "https://api.havirkesht.ir/farmers_load/",
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      console.log(response);

      return response.data;
    } catch (error: any) {
      const backendMessage = error?.response?.data?.detail;
      if (backendMessage === "Duplicate load_number for this factory and crop year") {
        return Swal.fire(
          "خطا",
          "بار با این شماره قبلاً برای این کارخانه در این سال زراعی ثبت شده است",
          "error"
        );
      }
      if (error.response.data.detail)
        console.error("Error adding data:", error);
      if (axios.isAxiosError(error)) {
        const message = error.response?.data?.message || "Network Error";
        return rejectWithValue({
          message,
          status: error.response?.status,
        });
      }
      return rejectWithValue({
        message: "An unknown error occurred",
      });
    }
  }
);

export const deleteBurdanFarmerReducer = createAsyncThunk(
  "dataBurdanFarmer/deleteData",
  async (dataID: string, { rejectWithValue }) => {
    try {
      await axios.delete(`https://api.havirkesht.ir/farmers_load/${dataID}`);
      return { dataID };
    } catch (error: any) {
      return rejectWithValue(error);
    }
  }
);

export const updateBurdanFarmerReducer = createAsyncThunk(
  "dataBurdanFarmer/updateData",
  async (data: { dataID: any }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `https://api.havirkesht.ir/farmers_load/${data.dataID}`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.log(error, "update error");
      return rejectWithValue(error);
    }
  }
);

const dataBurdanFarmerSlice = createSlice({
  name: "dataBurdanFarmer",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Get Data cases
      .addCase(getBurdanFarmerReducer.pending, (state) => {
        state.isLoading = true;
        state.error = "";
      })
      .addCase(getBurdanFarmerReducer.fulfilled, (state: any, action: any) => {
        state.isLoading = false;
        state.data = action.payload;
        state.success = true;
      })
      .addCase(getBurdanFarmerReducer.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || "Something went wrong";
        state.success = false;
      })

      // Add Data cases
      .addCase(addBurdanFarmerReducer.pending, (state) => {
        state.isLoading = true;
        state.error = "";
      })
      .addCase(addBurdanFarmerReducer.fulfilled, (state, action) => {
        state.isLoading = false;
        if (Array.isArray(state.data)) {
          state.data.push(action.payload);
        } else {
          state.data = [action.payload];
        }
        state.success = true;
      })
      .addCase(addBurdanFarmerReducer.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as { message: string })?.message ||
          "Failed to add data";
        state.success = false;
      })

      // Delete Data cases
      .addCase(deleteBurdanFarmerReducer.fulfilled, (state, action) => {
        state.data = state.data.filter(
          (item: any) => item.dataID !== action.payload.dataID
        );
      })

      // Update Data cases
      .addCase(updateBurdanFarmerReducer.fulfilled, (state, action) => {
        const index = state.data.findIndex(
          (item) => item.dataID === action.payload.dataID
        );
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      });
  },
});

export default dataBurdanFarmerSlice.reducer;
